function getNiceStep(roughStep) {
  if (!Number.isFinite(roughStep) || roughStep <= 0) return 1;

  const exponent = Math.floor(Math.log10(roughStep));
  const magnitude = 10 ** exponent;
  const fraction = roughStep / magnitude;

  let niceFraction;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 2.5) niceFraction = 2.5;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  return niceFraction * magnitude;
}

/**
 * Builds a "nice" axis domain and evenly spaced ticks for the given value range.
 */
function getNiceScale(minValue, maxValue, tickCount = 5) {
  let min = Number.isFinite(minValue) ? minValue : 0;
  let max = Number.isFinite(maxValue) ? maxValue : 1;

  if (min === max) {
    if (min === 0) {
      max = 1;
    } else {
      const padding = Math.abs(min) * 0.1 || 1;
      min -= padding;
      max += padding;
    }
  }

  const step = getNiceStep((max - min) / Math.max(1, tickCount));
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks = [];
  for (let value = niceMin; value <= niceMax + step / 2; value += step) {
    ticks.push(Number(value.toFixed(10)));
  }

  return { min: niceMin, max: niceMax, ticks };
}

/**
 * Splits a list of points into runs of consecutive points that have a defined
 * value for the given accessor, so lines can break across missing data.
 */
function getDefinedRuns(points, accessor) {
  const runs = [];
  let current = [];

  points.forEach((point) => {
    const value = accessor(point);
    if (value === undefined || value === null || Number.isNaN(value)) {
      if (current.length > 0) {
        runs.push(current);
        current = [];
      }
      return;
    }

    current.push(point);
  });

  if (current.length > 0) runs.push(current);

  return runs;
}

/**
 * Builds a smooth (Catmull-Rom to cubic bezier) SVG path for the given screen
 * coordinates. Falls back to a straight segment or a dot for short runs.
 */
function buildSmoothPath(coordinates) {
  if (coordinates.length === 0) return '';
  if (coordinates.length === 1) {
    const [x, y] = coordinates[0];
    return `M ${x} ${y} L ${x} ${y}`;
  }

  const tension = 0.2;
  let path = `M ${coordinates[0][0]} ${coordinates[0][1]}`;

  for (let i = 0; i < coordinates.length - 1; i += 1) {
    const [x0, y0] = coordinates[Math.max(0, i - 1)];
    const [x1, y1] = coordinates[i];
    const [x2, y2] = coordinates[i + 1];
    const [x3, y3] = coordinates[Math.min(coordinates.length - 1, i + 2)];

    const controlPoint1X = x1 + (x2 - x0) * tension;
    const controlPoint1Y = y1 + (y2 - y0) * tension;
    const controlPoint2X = x2 - (x3 - x1) * tension;
    const controlPoint2Y = y2 - (y3 - y1) * tension;

    path += ` C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${x2} ${y2}`;
  }

  return path;
}

export {
  buildSmoothPath,
  getDefinedRuns,
  getNiceScale,
};
